export const ROUTES = {
	SESSIONS: '/sessions',
	USER_ROLES: '/user-roles',
	PIPELINE: '/onboarding',
	SIGNUP: '/signup',
	LOGIN: '/login',
	// public: where an approve-by-email link lands (spec 17)
	PROFILE_CONFIRM: '/profile/confirm',
	SETTINGS: '/settings',
	CREDENTIALS: '/credentials',
	INVITES: '/invites',
	MINTCARTA: '/mint-carta',
	ON_BOARD: '/checkout',
	FLOW: '/flow',
	NO_ROUTE: '/',
	FUNDS: '/funds',
	FUNDSDETAILS: '/funds/:id',
	SIGNED_DOC: '/esign-templates',
	BILLING: '/billing',
	DASHBOARD: '/dashboard',
	INTEGRATE: '/integrate',
	Otp_Verify: '/otp-verify',
	CHOOSE_METHOD: '/choose-method',
	STAKEHOLDER: '/stakeholder',
	SHARECLASS: '/shareclass',
	EQUITYPLAN: '/equidityplan',
	CAPTABLE: 'captable',
	SCAN_QRCODE: '/scan_qrcode',
	QRCODE_FACE_DEMO: '/qrcode_face_demo',
	UPLODAD_DOCUMENTS: '/files-documents/:id',
	FACE_VERIFY_QR_CODE: '/face_verify_qrcode',
	RECIPIENT_FACE_VERIFY: '/face_verfify_recipient',
	API: '/api',
	ANALYTICS: '/analytics',
	ESIGN: '/esign',
	SUPPORT_AGENT: '/support-agent',
	SIGNED_DOC_INBOX: '/esign-sent',
	COMPLETED: '/completed',
	INBOX: '/esign-inbox',
	SIMPLICI_SIGN_DASHBOARD: '/esign-dashboard',
	SUB_ACCOUNT: '/sub-account',
	BUSINESS_PROCESSING: '/business-processing',
	BUSINESS_REJECTED: '/business-rejected',
	ESIGN_DRAFT: '/esign-draft',
	EVAULT: '/evault',
	/* The ownership-transfer charge records, moved off the billing page's tabs
	   onto their own eVault page. A STATIC segment under the same parent, so it
	   has to be registered BEFORE `/evault/:id` or the id route swallows
	   `transfers` as a vault id — see all-routes-constant. */
	EVAULT_TRANSFERS: '/evault/transfers',
	/* One vault's documents. A child route of EVAULT rather than a sibling, so
	   the permission key stays `evault` for both — see all-routes-constant. */
	EVAULT_DETAIL: '/evault/:id',
	TRANSACTION_MONITORING: '/transaction-monitoring',
	TM_INVESTIGATE: '/transaction-monitoring/investigate',
	TM_RULES: '/transaction-monitoring/rules',
	TM_HISTORICAL: '/transaction-monitoring/historical',
	TM_COUNTRY_COVERAGE: '/transaction-monitoring/country-coverage',
	DATA_PULLS: '/data-pulls',
};

export const ACTIVE_TABS = {
	PIPELINE: 'onboarding',
	CREDENTIALS: 'credentials',
	SESSIONS: 'sessions',
	SETTINGS: 'settings',
	INVITES: 'invites',
	MINTCARTA: 'mint-carta',
	USER_ROLES: 'user-roles',
	FUNDS: 'funds',
	/* dev added the eVault route; this merge kept the local copy of this file,
	   so the tab its route entries reference went missing and the build broke. */
	EVAULT: 'evault',
	/* A TAB NAME, NOT A PERMISSION KEY. Every other entry here doubles as one,
	   because `getCustomRoutes` looks routes up by the names in `customUserTab`
	   — but Transfers is a child of the `evault` route entry and answers to the
	   `evault` permission, exactly as `/evault/:id` does. This exists only so
	   TAB_PATH can resolve the sidebar row to `/evault/transfers` (the KEY has
	   to match ROUTES'), and so the two children carry distinct React keys and
	   distinct active-highlight paths. Registering it in ALL_ROUTES_MODULE would
	   make it a second permission key, which is exactly what we do not want. */
	EVAULT_TRANSFERS: 'evaultTransfers',
	TRANSACTION_MONITORING: 'transactionMonitoring',
	TM_INVESTIGATE: 'tmInvestigate',
	TM_RULES: 'tmRules',
	TM_HISTORICAL: 'tmHistorical',
	TM_COUNTRY_COVERAGE: 'tmCountryCoverage',
	DATA_PULLS: 'dataPulls',
	SIGNED_DOC_TEMPLATES: 'esign-templates',
	SIGNED_DOC_INBOX: 'esign-sent',
	BILLING: 'billing',
	INTEGRATE: 'integrate',
	STAKEHOLDER: 'stakeholder',
	SHARECLASS: 'shareclass',
	EQUITYPLAN: 'equidityplan',
	DASHBOARD: 'dashboard',
	API: 'api',
	ANALYTICS: 'analytics',
	SIMPLICI_SIGN: 'simplici-sign',
	SUPPORT_AGENT: 'support-agent',
	SIMPLICI_SIGN_DASHBOARD: 'esign-dashboard',
	INBOX: 'esign-inbox',
	SUB_ACCOUNT: 'sub-account',
	DRAFT: 'esign-draft'
};

/**
 * A sidebar tab name → the URL it actually navigates to.
 *
 * THE SIDEBAR USED TO ASSUME A TAB NAME *IS* ITS URL SEGMENT and navigate to
 * `/${tabName}`. That holds for almost every row by coincidence — `sessions`,
 * `evault`, `billing` are each both the permission key and the path segment —
 * and Transaction Monitoring is the one group that cannot satisfy it:
 *
 *   transactionMonitoring  →  /transaction-monitoring
 *   tmInvestigate          →  /transaction-monitoring/investigate
 *   tmRules                →  /transaction-monitoring/rules
 *
 * The names are camelCase because they are PERMISSION KEYS (they have to match
 * clients.api's roles model and seed-all.js's PERMISSION_KEYS), the paths are
 * kebab-case, and two of the three are NESTED — which no single segment can
 * express. So all three rows navigated to `/transactionMonitoring` and friends,
 * matched no route, and fell through to the catch-all redirect: every
 * Transaction Monitoring row in the sidebar silently landed on /dashboard.
 *
 * Derived from the two objects above rather than hand-listed, so a new route
 * cannot drift out of it: every ACTIVE_TABS entry whose KEY also exists in
 * ROUTES maps to that route's real path. For every row that already worked,
 * `ROUTES[K] === '/' + ACTIVE_TABS[K]`, so this returns exactly what the old
 * concatenation returned — the three entries above are the only behaviour
 * change.
 */
export const TAB_PATH: Record<string, string> = Object.entries(ACTIVE_TABS).reduce(
	(acc, [key, tab]) => {
		const path = (ROUTES as Record<string, string>)[key];
		if (path) acc[tab] = path;
		return acc;
	},
	{} as Record<string, string>
);

/**
 * Where a sidebar row should go. Falls back to the old `/${tabName}` for the few
 * ACTIVE_TABS entries with no ROUTES counterpart (`esign-templates`,
 * `simplici-sign`, `esign-draft`), so nothing that worked before changes.
 */
export const pathForTab = (tabName: string): string =>
	TAB_PATH[tabName] ?? `/${tabName}`;

export const SIDEBAR_TABS = {
	label: 'Main Menu',
	tabs: [
		{
			ROUTE: ACTIVE_TABS.DASHBOARD,
			TITLE: 'Dashboard',
			ICON: 'ri-dashboard-',
			TAB_INDEX: 13,
		},
		{
			ROUTE: ACTIVE_TABS.PIPELINE,
			TITLE: 'Onboarding',
			ICON: 'ri-archive-stack-',
			TAB_INDEX: 1,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.PIPELINE,
					TITLE: 'Onboarding Flow',
					ICON: 'ri-list-check-2',
				},
				{
					ROUTE: ACTIVE_TABS.SESSIONS,
					TITLE: 'Sessions',
					ICON: 'ri-contacts-',
				},
				{
					ROUTE: ACTIVE_TABS.FUNDS,
					TITLE: 'Funds',
					ICON: 'ri-funds-',
				},
			],
		},
		{
			ROUTE: ACTIVE_TABS.SIMPLICI_SIGN,
			TITLE: 'esign',
			ICON: 'ri-draft-',
			TAB_INDEX: 15,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.SIMPLICI_SIGN_DASHBOARD,
					TITLE: 'esign Dashboard',
					ICON: 'ri-draft-',
				},
				{
					ROUTE: ACTIVE_TABS.SIGNED_DOC_TEMPLATES,
					TITLE: 'esign Templates',
					ICON: 'ri-article-',
				},
				{
					ROUTE: ACTIVE_TABS.DRAFT,
					TITLE: 'Draft',
					ICON: 'ri-mail-send-',
				},
				{
					ROUTE: ACTIVE_TABS.INBOX,
					TITLE: 'Inbox',
					ICON: 'ri-inbox-',
				},
				{
					ROUTE: ACTIVE_TABS.SIGNED_DOC_INBOX,
					TITLE: 'Sent',
					ICON: 'ri-mail-send-',
				},
			],
		},
		/* eVault is a MAIN-MENU row, not an eSign sub-item.
		   It is its own product surface — its own permission key, its own paid
		   add-on, and a store people open to find an original rather than to do
		   anything with an envelope. As a sub-item it sat one click deep behind a
		   collapsed group, which is the wrong depth for something billed
		   separately. Placed immediately after eSign because that is still where
		   its documents come from.
		   Note the map that has to move with it: a row's visibility is decided by
		   matching its TITLE against PARENT_SIDEBAR for top-level rows and
		   SUB_SIDEBAR for sub-rows, so `evault` moves from the second to the
		   first. Leaving it in SUB_SIDEBAR would render nothing at all.

		   NOW A GROUP, with the drive and Transfers beneath it — so it needs
		   BOTH maps: PARENT_SIDEBAR for this header, SUB_SIDEBAR for the two
		   children. Unlike Txn Monitoring's group, both children hang off the
		   ONE `evault` permission key (they are the same product surface and the
		   same route entry), which is why SUB_SIDEBAR maps that key to a LIST of
		   two labels rather than one string. A second key would have to exist on
		   clients.api's roles model and in seed-all.js's PERMISSION_KEYS before
		   it granted anything, and until then the row would silently not
		   render — the exact failure the note below describes. */
		{
			ROUTE: ACTIVE_TABS.EVAULT,
			TITLE: 'eVault',
			ICON: 'ri-safe-',
			TAB_INDEX: 50,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.EVAULT,
					TITLE: 'eVault dashboard',
					ICON: 'ri-safe-',
				},
				{
					ROUTE: ACTIVE_TABS.EVAULT_TRANSFERS,
					TITLE: 'Transfers',
					ICON: 'ri-exchange-',
				},
			],
		},
		{
			/* A GROUP row, so it renders as a header with its pages beneath —
			   sidebar.tsx only navigates a row that has no SUB_ROUTE (the
			   `if (!(SUB_ROUTE && isOpen && windowSizeWidth > 1024))` guard on
			   its onClick), which is the same shape esign and Settings use.
			   EVERY child needs THREE entries: a SUB_SIDEBAR label, a
			   PARENT_SIDEBAR mapping to this TITLE, and a SUB_ROUTE row below —
			   in BOTH this tree and SIDEBAR_COLLAPSE_TABS, which sidebar.tsx
			   picks between on `isOpen`.

			   SUB_ROUTE is the one with no other symptom. `tmRules` had its
			   permission key, its route, its SUB_SIDEBAR label and its
			   PARENT_SIDEBAR parent, and its page loaded fine when navigated to
			   directly — but with no SUB_ROUTE entry there was no row to render,
			   so the sidebar just showed two children instead of three, looking
			   exactly like a page that had never been built.

			   The titles here are matched by VALUE against SUB_SIDEBAR
			   (`conditionalActiveTab` compares `el === subRoute.TITLE`), so a
			   child's TITLE and its SUB_SIDEBAR string must stay identical —
			   renaming one alone silently drops the row. */
			ROUTE: ACTIVE_TABS.TRANSACTION_MONITORING,
			TITLE: 'Txn Monitoring',
			ICON: 'ri-radar-',
			TAB_INDEX: 51,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.TRANSACTION_MONITORING,
					TITLE: 'Transaction investigate',
					ICON: 'ri-alarm-warning-',
					TAB_INDEX: 51,
				},
				{
					ROUTE: ACTIVE_TABS.TM_INVESTIGATE,
					TITLE: 'Subject Investigate',
					ICON: 'ri-search-eye-',
					TAB_INDEX: 52,
				},
				{
					ROUTE: ACTIVE_TABS.TM_RULES,
					TITLE: 'TM rules',
					ICON: 'ri-shield-check-',
					TAB_INDEX: 53,
				},
				{
					ROUTE: ACTIVE_TABS.TM_HISTORICAL,
					TITLE: 'TM historical',
					ICON: 'ri-upload-cloud-2-',
					TAB_INDEX: 54,
				},
				{
					ROUTE: ACTIVE_TABS.TM_COUNTRY_COVERAGE,
					TITLE: 'Country coverage',
					ICON: 'ri-global-',
					TAB_INDEX: 55,
				},
			],
		},
		{
			ROUTE: ACTIVE_TABS.SETTINGS,
			TITLE: 'Settings',
			ICON: 'ri-settings-5-',
			TAB_INDEX: 3,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.SETTINGS,
					TITLE: 'General Settings',
					ICON: 'ri-settings-5-',
				},
				{
					ROUTE: ACTIVE_TABS.CREDENTIALS,
					TITLE: 'Developers Keys',
					ICON: 'ri-profile-',
				},
				{
					ROUTE: ACTIVE_TABS.USER_ROLES,
					TITLE: 'User Roles',
					ICON: 'ri-team-',
				},
				{
					ROUTE: ACTIVE_TABS.SUB_ACCOUNT,
					TITLE: 'Sub Accounts',
					ICON: 'ri-briefcase-2-',
				},
			],
		},

		{
			ROUTE: ACTIVE_TABS.INTEGRATE,
			TITLE: 'Integrate',
			ICON: 'ri-guide-',
			TAB_INDEX: 8,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.INTEGRATE,
					TITLE: 'Embed Web',
					ICON: 'ri-settings-5-',
				},
				{
					ROUTE: ACTIVE_TABS.API,
					TITLE: 'API Docs',
					ICON: 'ri-settings-5-',
				},
			],
		},
		{
			ROUTE: ACTIVE_TABS.ANALYTICS,
			TITLE: 'Insights',
			ICON: 'ri-bar-chart-2-',
			TAB_INDEX: 14,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.ANALYTICS,
					TITLE: 'User Analytics',
					ICON: 'ri-bar-chart-2-',
				},
				{
					ROUTE: ACTIVE_TABS.SUPPORT_AGENT,
					TITLE: 'Support Agent',
					ICON: 'ri-user-star-',
				},
			],
		},

		{
			ROUTE: ACTIVE_TABS.BILLING,
			TITLE: 'Billing',
			ICON: 'ri-bank-card-',
			TAB_INDEX: 9,
		},
		/* Back in the main menu, under Billing. It went to the Account group when
		   that group still held User Profile and Logout; with those folded into
		   the footer it was the only row left, so "Account" was a heading over a
		   single item that is not account management anyway. */
		{
			/* ROUTE is the name, not a path: `handleChangeTab` intercepts it and
			   opens the demo modal, the same way it intercepts Logout. Main-menu
			   rows call `handleChangeTab(ROUTE)`, so without one this row rendered
			   and did nothing. */
			ROUTE: 'Request Demo',
			TITLE: 'Request Demo',
			ICON: 'ri-customer-service-2-line',
			TAB_INDEX: 4,
		},
		{
			/* INTERNAL ONLY. A staff diagnostic for our own nightly collectors —
			   not a tenant feature, which is why it sits outside Transaction
			   Monitoring rather than under it: the pulls it reports on are not all
			   TM's (aml.api refreshes sanctions nightly, clients.api bills
			   monthly), and burying a platform-wide view inside one product's
			   sub-menu would have made the others look like TM's business.

			   `PILL` is read by the sidebar row renderer. The gate that actually
			   restricts this is the @satschel.com check on `customUserTab` plus
			   the middleware on every /data-pulls endpoint — a hidden row is not
			   access control. */
			ROUTE: ACTIVE_TABS.DATA_PULLS,
			TITLE: 'Data Pulls',
			ICON: 'ri-database-2-line',
			TAB_INDEX: 5,
			PILL: 'internal only',
		},
	],
};

export const SIDEBAR_COLLAPSE_TABS = {
	label: 'Main Menu',
	tabs: [
		{
			ROUTE: ACTIVE_TABS.DASHBOARD,
			TITLE: 'Dashboard',
			ICON: 'ri-dashboard-',
			TAB_INDEX: 13,
			SUB_ROUTE: [],
		},
		{
			ROUTE: ACTIVE_TABS.PIPELINE,
			TITLE: 'Onboarding',
			ICON: 'ri-archive-stack-',
			TAB_INDEX: 1,
		},
		{
			ROUTE: ACTIVE_TABS.SESSIONS,
			TITLE: 'Sessions',
			ICON: 'ri-contacts-',
			TAB_INDEX: 6,
		},
		{
			ROUTE: ACTIVE_TABS.FUNDS,
			TITLE: 'Funds',
			ICON: 'ri-funds-',
			TAB_INDEX: 2,
		},
		{
			ROUTE: ACTIVE_TABS.SIMPLICI_SIGN_DASHBOARD,
			TITLE: 'esign Dashboard',
			ICON: 'ri-draft-',
			TAB_INDEX: 3,
		},
		{
			ROUTE: ACTIVE_TABS.SIGNED_DOC_TEMPLATES,
			TITLE: 'esign Templates',
			ICON: 'ri-article-',
			TAB_INDEX: 18,
		},
		{
			ROUTE: ACTIVE_TABS.DRAFT,
			TITLE: 'Draft',
			ICON: 'ri-mail-send-',
			TAB_INDEX: 99,
		},
		{
			ROUTE: ACTIVE_TABS.INBOX,
			TITLE: 'Inbox',
			ICON: 'ri-inbox-',
			TAB_INDEX: 99,
		},
		{
			ROUTE: ACTIVE_TABS.SIGNED_DOC_INBOX,
			TITLE: 'Sent',
			ICON: 'ri-mail-send-',
			TAB_INDEX: 49,
		},
		/* FLATTENED, NOT A GROUP — the way esign's five pages are listed above.
		   This tree is only ever read when the sidebar is CLOSED, and `sidebar.tsx`
		   renders `ToggleRoute` (the thing that draws SUB_ROUTE) only when
		   `SUB_ROUTE && isOpen && windowSizeWidth > 1024`. A group here therefore
		   draws one icon and swallows its children: eVault's two pages had no
		   collapsed entry at all. Each page is its own row instead, gated by
		   MOBILE_VIEW_TABS — which is what `conditionalActiveTab` consults when the
		   sidebar is closed — and that map carries all three labels on the one
		   `evault` key for exactly this reason. */
		{
			ROUTE: ACTIVE_TABS.EVAULT,
			TITLE: 'eVault dashboard',
			ICON: 'ri-safe-',
			TAB_INDEX: 50,
		},
		{
			ROUTE: ACTIVE_TABS.EVAULT_TRANSFERS,
			TITLE: 'Transfers',
			ICON: 'ri-exchange-',
			TAB_INDEX: 50,
		},
		{
			/* A GROUP row, so it renders as a header with its pages beneath —
			   sidebar.tsx only navigates a row that has no SUB_ROUTE (the
			   `if (!(SUB_ROUTE && isOpen && windowSizeWidth > 1024))` guard on
			   its onClick), which is the same shape esign and Settings use.
			   EVERY child needs THREE entries: a SUB_SIDEBAR label, a
			   PARENT_SIDEBAR mapping to this TITLE, and a SUB_ROUTE row below —
			   in BOTH this tree and SIDEBAR_COLLAPSE_TABS, which sidebar.tsx
			   picks between on `isOpen`.

			   SUB_ROUTE is the one with no other symptom. `tmRules` had its
			   permission key, its route, its SUB_SIDEBAR label and its
			   PARENT_SIDEBAR parent, and its page loaded fine when navigated to
			   directly — but with no SUB_ROUTE entry there was no row to render,
			   so the sidebar just showed two children instead of three, looking
			   exactly like a page that had never been built.

			   The titles here are matched by VALUE against SUB_SIDEBAR
			   (`conditionalActiveTab` compares `el === subRoute.TITLE`), so a
			   child's TITLE and its SUB_SIDEBAR string must stay identical —
			   renaming one alone silently drops the row. */
			ROUTE: ACTIVE_TABS.TRANSACTION_MONITORING,
			TITLE: 'Txn Monitoring',
			ICON: 'ri-radar-',
			TAB_INDEX: 51,
			SUB_ROUTE: [
				{
					ROUTE: ACTIVE_TABS.TRANSACTION_MONITORING,
					TITLE: 'Transaction investigate',
					ICON: 'ri-alarm-warning-',
					TAB_INDEX: 51,
				},
				{
					ROUTE: ACTIVE_TABS.TM_INVESTIGATE,
					TITLE: 'Subject Investigate',
					ICON: 'ri-search-eye-',
					TAB_INDEX: 52,
				},
				{
					ROUTE: ACTIVE_TABS.TM_RULES,
					TITLE: 'TM rules',
					ICON: 'ri-shield-check-',
					TAB_INDEX: 53,
				},
				{
					ROUTE: ACTIVE_TABS.TM_HISTORICAL,
					TITLE: 'TM historical',
					ICON: 'ri-upload-cloud-2-',
					TAB_INDEX: 54,
				},
				{
					ROUTE: ACTIVE_TABS.TM_COUNTRY_COVERAGE,
					TITLE: 'Country coverage',
					ICON: 'ri-global-',
					TAB_INDEX: 55,
				},
			],
		},

		{
			ROUTE: ACTIVE_TABS.SETTINGS,
			TITLE: 'Settings',
			ICON: 'ri-settings-5-',
			TAB_INDEX: 12,
		},
		{
			ROUTE: ACTIVE_TABS.CREDENTIALS,
			TITLE: 'Developer Keys',
			ICON: 'ri-profile-',
			TAB_INDEX: 4,
		},
		{
			ROUTE: ACTIVE_TABS.USER_ROLES,
			TITLE: 'User Roles',
			ICON: 'ri-team-',
			TAB_INDEX: 7,
		},
		{
			ROUTE: ACTIVE_TABS.SUB_ACCOUNT,
			TITLE: 'Sub Account',
			ICON: 'ri-briefcase-2-',
			TAB_INDEX: 19,
		},
		{
			ROUTE: ACTIVE_TABS.INTEGRATE,
			TITLE: 'Integrate',
			ICON: 'ri-guide-',
			TAB_INDEX: 9,
		},
		{
			ROUTE: ACTIVE_TABS.API,
			TITLE: 'Api Docs',
			ICON: 'ri-pages-',
			TAB_INDEX: 10,
		},
		{
			ROUTE: ACTIVE_TABS.ANALYTICS,
			TITLE: 'User Analytics',
			ICON: 'ri-bar-chart-2-',
			TAB_INDEX: 15,
		},
		{
			ROUTE: ACTIVE_TABS.SUPPORT_AGENT,
			TITLE: 'Support Agent',
			ICON: 'ri-user-star-',
			TAB_INDEX: 16,
		},
		{
			ROUTE: ACTIVE_TABS.BILLING,
			TITLE: 'Billing',
			ICON: 'ri-bank-card-',
			TAB_INDEX: 11,
		},
		{
			/* Also in the COLLAPSED tree, or the tab disappears whenever somebody
			   narrows the sidebar — Request Demo is absent here because it is a CTA
			   rather than a destination, but this one is a real page. The pill does
			   not render collapsed (icons only), which is fine: the restriction is
			   enforced on the request, not by the label. */
			ROUTE: ACTIVE_TABS.DATA_PULLS,
			TITLE: 'Data Pulls',
			ICON: 'ri-database-2-line',
			TAB_INDEX: 5,
			PILL: 'internal only',
		},
	],
};

/* The Account group is empty by design. Its three rows all moved: User Profile
   and Logout into the footer where the identity they act on lives, and Request
   Demo back to the main menu under Billing. Kept as an empty list rather than
   deleted because `mapProfileTabs` still maps over it, and an empty group
   renders nothing. */
export const SIDEBAR_ACCOUNT_TABS = {
	label: 'Account',
	tabs: [] as Array<{
		TITLE: string;
		ICON: string;
		TAB_INDEX: number;
		isWebcomponentShow?: boolean;
	}>,
};

/**
 * Permission key → the sub-row label(s) it unlocks.
 *
 * A LIST where one key owns more than one row. eVault's two pages are one
 * permission and one route entry (`/evault` with children, see
 * all-routes-constant), so there is no second key to hang the second label on —
 * and inventing one would mean adding it to clients.api's roles model and
 * seed-all.js's PERMISSION_KEYS before it granted anything, with the row
 * silently absent in the meantime. toggle-route.tsx flattens this; every
 * single-row key stays a plain string.
 */
export const SUB_SIDEBAR: Record<string, string | string[]> = {
	onboarding: 'Onboarding Flow',
	session: 'Sessions',
	funds: 'Funds',
	signDashboard: 'esign Dashboard',
	template: 'esign Templates',
	inbox: 'Inbox',
	sent: 'Sent',
	generalSettings: 'General Settings',
	developersKeys: 'Developers Keys',
	userRoles: 'User Roles',
	embedWeb: 'Embed Web',
	apiDocs: 'API Docs',
	// userAnalytics: 'User Analytics',
	// supportAgent: 'Support Agent',
	subAccount: 'Sub Accounts',
	draft: 'Draft',
	/* Both eVault pages, on the one `evault` permission — see the note above. */
	evault: ['eVault dashboard', 'Transfers'],
	transactionMonitoring: 'Transaction investigate',
	tmInvestigate: 'Subject Investigate',
	tmRules: 'TM rules',
	tmHistorical: 'TM historical',
	tmCountryCoverage: 'Country coverage',
	dataPulls: 'Data Pulls',
};

export const PARENT_SIDEBAR: Record<string, string> = {
	dashboard: 'Dashboard',
	onboarding: 'Onboarding',
	session: 'Onboarding',
	funds: 'Onboarding',
	signDashboard: 'esign',
	template: 'esign',
	inbox: 'esign',
	sent: 'esign',
	generalSettings: 'Settings',
	developersKeys: 'Settings',
	userRoles: 'Settings',
	embedWeb: 'Integrate',
	apiDocs: 'Integrate',
	// userAnalytics: 'Insights',
	// supportAgent: 'Insights',
	billing: 'Billing',
	subAccount: 'Sub Account',
	draft: 'esign',
	/* Top-level row now, so its own TITLE rather than the eSign group's. */
	evault: 'eVault',
	transactionMonitoring: 'Txn Monitoring',
	tmInvestigate: 'Txn Monitoring',
	tmRules: 'Txn Monitoring',
	tmHistorical: 'Txn Monitoring',
	tmCountryCoverage: 'Txn Monitoring',
	dataPulls: 'Data Pulls',
};

/**
 * The tab labels shown when the sidebar is COLLAPSED, or the viewport is 1024px
 * or narrower — `sidebar.tsx`'s `conditionalActiveTab` picks this over
 * PARENT_SIDEBAR in both cases. Same list-or-string shape as SUB_SIDEBAR, and
 * for the same reason: one permission key can own more than one row.
 */
export const MOBILE_VIEW_TABS: Record<string, string | string[]> = {
	dashboard: 'Dashboard',
	onboarding: 'Onboarding',
	session: 'Sessions',
	funds: 'Funds',
	signDashboard: 'esign Dashboard',
	template: 'esign Templates',
	inbox: 'Inbox',
	sent: 'Sent',
	generalSettings: 'Settings',
	developersKeys: 'Developers Keys',
	userRoles: 'User Roles',
	embedWeb: 'Integrate',
	apiDocs: 'API Docs',
	// userAnalytics: 'User Analytics',
	// supportAgent: 'Support Agent',
	billing: 'Billing',
	subAccount: 'Sub Account',
	darft: 'Draft',
	/* THREE labels on the one key, and each is needed at a different width.
	   `conditionalActiveTab` reads this map whenever the sidebar is closed OR the
	   viewport is 1024px or narrower, and matches a row's TITLE against it:
	   'eVault dashboard' and 'Transfers' are the two flattened rows in
	   SIDEBAR_COLLAPSE_TABS, and 'eVault' is the GROUP row in SIDEBAR_TABS, which
	   renders as a plain navigating row at narrow widths (ToggleRoute is gated on
	   > 1024px). Dropping the bare 'eVault' would make the product vanish from a
	   narrow open sidebar entirely. */
	evault: ['eVault', 'eVault dashboard', 'Transfers'],
};
